import {CheckCircle, Coffee, Utensils} from "lucide-react";
import {zodResolver} from "@hookform/resolvers/zod";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {Dialog, DialogContent} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Textarea} from "@/components/ui/textarea";
import {
  ComplianceGateActionButton,
  ComplianceGateActionError,
  ComplianceGateDialogHeader,
  ComplianceReconcileOverlay,
  CoordinatesRequiredGate,
  complianceGateDialogContentClassName,
} from "@/src/modules/core/components";
import {useGateSubformCoordinatesPrewarm, prewarmGateCoordinatesOnPointerDown} from "@/src/modules/core/hooks/use-coordinates-gate";
import {interruptSchema} from "@/src/modules/officer/schema";
import {officerService} from "@/src/modules/officer/services";
import {BreakAction} from "@/src/modules/officer/types";
import {ShiftBreak} from "@/src/modules/shifts/types";

interface BreakActiveModalProps {
  open: boolean;
  showReconcilingOverlay?: boolean;
  isPending: boolean;
  actionBusy: boolean;
  gpsBusy?: boolean;
  submitBlockedByLocation: boolean;
  shiftBreak: ShiftBreak;
  actionError?: {message: string; action: BreakAction} | null;
  onClearActionError?: () => void;
  onComplete: () => void;
  onInterrupt: (reason: string) => void;
  /** Fires once when the scheduled break duration elapses (server may have auto-completed). */
  onBreakTimeElapsed?: () => void;
}

export function BreakActiveModal({
  open,
  showReconcilingOverlay,
  isPending,
  actionBusy,
  gpsBusy = false,
  submitBlockedByLocation,
  shiftBreak,
  actionError,
  onClearActionError,
  onComplete,
  onInterrupt,
  onBreakTimeElapsed,
}: BreakActiveModalProps) {
  const isInterruptAvailable = officerService.getIsInterruptAvailable(shiftBreak);
  const [showInterruptForm, setShowInterruptForm] = useState(false);
  useGateSubformCoordinatesPrewarm(true, showInterruptForm, submitBlockedByLocation);
  const [pendingAction, setPendingAction] = useState<"complete" | "interrupt" | null>(null);
  const isBusy = isPending || actionBusy;
  const [isBreakTimeElapsed, setIsBreakTimeElapsed] = useState(false);
  const [remainingTime, setRemainingTime] = useState(() => {
    return officerService.getBreakRemainingTime(shiftBreak);
  });
  const elapsedNotifiedForBreakId = useRef<number | null>(null);

  useEffect(() => {
    if (!isBreakTimeElapsed) {
      return;
    }
    setShowInterruptForm(false);
    if (actionError?.action === BreakAction.INTERRUPT) {
      onClearActionError?.();
    }
  }, [isBreakTimeElapsed, actionError?.action, onClearActionError]);

  const visibleActionError =
    actionError &&
    (showInterruptForm
      ? actionError.action === BreakAction.INTERRUPT
      : actionError.action === BreakAction.COMPLETE)
      ? actionError.message
      : null;

  useEffect(() => {
    elapsedNotifiedForBreakId.current = null;
    setIsBreakTimeElapsed(false);
  }, [shiftBreak.shift_break_id]);

  useEffect(() => {
    if (!isBusy) {
      setPendingAction(null);
    }
  }, [isBusy]);

  useEffect(() => {
    const current =
      remainingTime ?? officerService.getBreakRemainingTime(shiftBreak);
    const isElapsed = !current || current.asMinutes() <= 0;

    if (!isElapsed) return;

    if (!isBreakTimeElapsed) {
      setIsBreakTimeElapsed(true);
    }

    if (
      onBreakTimeElapsed &&
      elapsedNotifiedForBreakId.current !== shiftBreak.shift_break_id
    ) {
      elapsedNotifiedForBreakId.current = shiftBreak.shift_break_id;
      onBreakTimeElapsed();
    }
  }, [
    shiftBreak.shift_break_id,
    shiftBreak.duration_minutes,
    shiftBreak.execution_start_datetime,
    remainingTime,
    isBreakTimeElapsed,
    onBreakTimeElapsed,
  ]);

  useEffect(() => {
    if (!shiftBreak.execution_start_datetime) return;

    const interval = setInterval(() => {
      const nextRemaining = officerService.getBreakRemainingTime(shiftBreak);
      setRemainingTime(nextRemaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [shiftBreak]);

  const breakType = shiftBreak.break_type === "lunch" ? "meal" : "rest";
  const Icon = shiftBreak.break_type === "lunch" ? Utensils : Coffee;

  return (
    <Dialog modal open={open} onOpenChange={() => {}}>
      <DialogContent
        className={complianceGateDialogContentClassName}
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-4">
          <ComplianceGateDialogHeader
            icon={Icon}
            title="Break in progress"
            description={`You are currently on a ${breakType} break`}
          />

          {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}

          {!showInterruptForm ? (
            <fieldset disabled={submitBlockedByLocation} className="m-0 border-0 p-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center rounded-lg border border-border bg-muted/40 px-4 py-6">
                  {remainingTime && remainingTime.asSeconds() > 0 ? (
                    <>
                      <span className="text-3xl font-bold tabular-nums">
                        {remainingTime.humanize()}
                      </span>
                      <span className="text-sm text-muted-foreground">remaining</span>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">Your break is over</span>
                  )}
                </div>

                {isBreakTimeElapsed ? (
                  <ComplianceGateActionButton
                    className="w-full gap-2"
                    isLoading={pendingAction === "complete"}
                    isOtherLoading={isBusy && pendingAction !== "complete"}
                    loadingLabel={gpsBusy ? "Getting location…" : undefined}
                    onClick={() => {
                      setPendingAction("complete");
                      void onComplete();
                    }}
                  >
                    <CheckCircle className="size-4" />
                    Complete break
                  </ComplianceGateActionButton>
                ) : (
                  <Button
                    variant="destructive"
                    className="w-full"
                    disabled={!isInterruptAvailable || isBusy}
                    {...prewarmGateCoordinatesOnPointerDown()}
                    onClick={() => isInterruptAvailable && setShowInterruptForm(true)}
                  >
                    Interrupt break
                  </Button>
                )}
              </div>
            </fieldset>
          ) : (
            <fieldset disabled={submitBlockedByLocation} className="m-0 border-0 p-0">
              <InterruptForm
                isLoading={pendingAction === "interrupt"}
                isOtherLoading={isBusy && pendingAction !== "interrupt"}
                onCancel={() => {
                  setShowInterruptForm(false);
                  onClearActionError?.();
                }}
                onSubmit={(data) => {
                  setPendingAction("interrupt");
                  void onInterrupt(data.reason);
                }}
              />
            </fieldset>
          )}

          {visibleActionError ? (
            <ComplianceGateActionError message={visibleActionError} />
          ) : null}
        </div>
        {showReconcilingOverlay ? <ComplianceReconcileOverlay /> : null}
      </DialogContent>
    </Dialog>
  );
}

interface InterruptFormProps {
  isLoading: boolean;
  isOtherLoading: boolean;
  onCancel: () => void;
  onSubmit: (data: z.infer<typeof interruptSchema>) => void;
}

function InterruptForm({isLoading, isOtherLoading, onCancel, onSubmit}: InterruptFormProps) {
  const form = useForm<z.infer<typeof interruptSchema>>({
    defaultValues: {reason: ""},
    resolver: zodResolver(interruptSchema),
  });

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="reason"
          render={({field, formState}) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <FormControl>
                <Textarea rows={5} className="resize-none" {...field} />
              </FormControl>
              {formState.errors.reason && <FormError error={formState.errors.reason.message} />}
            </FormItem>
          )}
        />

        <div className="flex justify-end items-center gap-2">
          <Button
            variant="ghost"
            type="button"
            disabled={isLoading || isOtherLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <ComplianceGateActionButton
            type="submit"
            isLoading={isLoading}
            isOtherLoading={isOtherLoading}
          >
            Confirm interrupt
          </ComplianceGateActionButton>
        </div>
      </form>
    </Form>
  );
}
